import React, { type Dispatch, type SetStateAction, useState } from 'react';
import {
  ApplicationIcon,
  Box,
  Button,
  Chip,
  Icon,
  Menu,
  MenuItem,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import { useHistory } from 'react-router-dom';

import { useInvertedLocalize } from '../../../../../utils';
import {
  initialMenuOptions,
  PROVIDERS_STATUS,
} from '../../../data-layer/constants';
import {
  type Provider,
  type ProviderResponse,
} from '../../../data-layer/types';
import { useUpdateIDPProvider } from '../../../hooks';

import invertedMessages from './messages';

import './ProviderCard.css';

interface ProviderCardProps {
  provider: Provider | undefined;
  item: ProviderResponse;
  setErrorMessage: (str: string) => void;
  setAlertTitle: Dispatch<SetStateAction<string>>;
  setAlertStatus: Dispatch<SetStateAction<string>>;
  setShowCriticalBanner: Dispatch<SetStateAction<boolean>>;
  setIsEnableSuccessVisible: Dispatch<SetStateAction<boolean>>;
  handleConfigureClick: (a: ProviderResponse) => void;
  refetchIDPProviders: () => void;
}

function ProviderCard({
  provider,
  item,
  setErrorMessage,
  setShowCriticalBanner,
  setIsEnableSuccessVisible,
  setAlertTitle,
  setAlertStatus,
  handleConfigureClick,
  refetchIDPProviders,
}: ProviderCardProps) {
  const [isOpenEnableModal, setIsOpenEnableModal] = useState(false);
  const [isOpenDisableModal, setIsOpenDisableModal] = useState(false);
  const l = useInvertedLocalize(invertedMessages);
  const { mutate: updateIDPConfig } = useUpdateIDPProvider();
  const history = useHistory();

  const updatedMenuOptions = initialMenuOptions.map((opt) => ({
    ...opt,
    label: l(opt.label as keyof typeof invertedMessages),
  }));

  const enableModal = () => (
    <Box className='Ext-IDPProviders-setupState'>
      <Button
        className='Ext-IDPProviders-setupState-button'
        type='button'
        kind='subtle'
        size='medium'
        onClick={() => setIsOpenEnableModal(true)}>
        {l('Enable')}
      </Button>
      <ModalIsland
        kind='warning'
        alertTitle={l('Are you sure you want to enable ID proofing?')}
        alertSubtitle={l('Third party cost may be incurred.')}
        onClose={() => setIsOpenEnableModal(false)}
        isOpen={isOpenEnableModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => {
              if (provider) {
                const providerConfig = item;

                providerConfig.status = 1;

                if (
                  providerConfig.config &&
                  'PASSWORD' in providerConfig.config
                ) {
                  delete providerConfig.config.PASSWORD;
                }

                if (
                  providerConfig.config &&
                  'LICENSE' in providerConfig.config
                ) {
                  delete providerConfig.config.LICENSE;
                }

                updateIDPConfig(providerConfig, {
                  onSuccess(response) {
                    if (response.errorMessage) {
                      setErrorMessage(response.errorMessage);
                      setShowCriticalBanner(true);
                      setIsOpenEnableModal(false);
                    } else {
                      setIsEnableSuccessVisible(true);
                      setAlertTitle(provider.name);
                      setAlertStatus(l('Enabled'));
                      setIsOpenEnableModal(false);
                      refetchIDPProviders();
                    }
                  },
                });
              }
            }}>
            {l('Apply')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            onClick={() => setIsOpenEnableModal(false)}
            size='medium'>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );

  const disableModal = () => (
    <Box className='Ext-IDPProviders-setupState'>
      <Button
        className='Ext-IDPProviders-setupState-button'
        type='button'
        kind='subtle'
        size='medium'
        onClick={() => {
          setIsOpenDisableModal(true);
        }}>
        {l('Disable')}
      </Button>
      <ModalIsland
        kind='warning'
        alertTitle={l('Are you sure you want to disable ID proofing?')}
        alertSubtitle=''
        onClose={() => setIsOpenDisableModal(false)}
        isOpen={isOpenDisableModal}
        primaryButton={
          <Button
            type='button'
            kind='outlined'
            size='medium'
            onClick={() => {
              if (provider) {
                const providerConfig = item;

                providerConfig.status = 0;

                if (
                  providerConfig.config &&
                  'PASSWORD' in providerConfig.config
                ) {
                  delete providerConfig.config.PASSWORD;
                }

                if (
                  providerConfig.config &&
                  'LICENSE' in providerConfig.config
                ) {
                  delete providerConfig.config.LICENSE;
                }

                updateIDPConfig(providerConfig, {
                  onSuccess(response) {
                    if (response.errorMessage) {
                      setErrorMessage(response.errorMessage);
                      setShowCriticalBanner(true);
                      setIsOpenDisableModal(false);
                    } else {
                      setIsEnableSuccessVisible(true);
                      setAlertTitle(provider.name);
                      setAlertStatus(l('Disabled'));
                      setIsOpenDisableModal(false);
                      refetchIDPProviders();
                    }
                  },
                });
              }
            }}>
            {l('Disable')}
          </Button>
        }
        secondaryButton={
          <Button
            type='button'
            kind='ghost'
            onClick={() => setIsOpenDisableModal(false)}
            size='medium'>
            {l('Cancel')}
          </Button>
        }
      />
    </Box>
  );

  const setUpHandler = () => (
    <Box className='Ext-IDPProviders-setupState'>
      <Button
        className='Ext-IDPProviders-setupState-button'
        type='button'
        kind='subtle'
        size='medium'
        onClick={() => {
          if (provider?.vendorName) {
            history.push({
              pathname: `/idproofing/providers/settings`,
              search: `?id=${provider.vendorName}`,
            });
          }
        }}>
        {l('Set Up')}
      </Button>
    </Box>
  );

  return (
    <Box
      className='Ext-IDPProviders-contentWrapper-content-card'
      dataTestId={item.vendorName}
      key={item.vendorName}>
      <Box className='Ext-IDPProviders-contentWrapper-content-card-controller'>
        {item.status === 0 ? (
          <Chip size='xSmall' label={l(PROVIDERS_STATUS.DISABLED)} />
        ) : (
          item.status !== 2 && (
            <Chip
              size='xSmall'
              color='success'
              label={l(PROVIDERS_STATUS.ENABLED)}
            />
          )
        )}
        {item.status !== 2 && (
          <Menu
            className='Ext-IDPProviders-contentWrapper-content-card-controller-menu'
            CustomMenu={
              <>
                {updatedMenuOptions.map((option) => (
                  <React.Fragment key={option.value}>
                    <MenuItem
                      key={option.value}
                      option={option}
                      onClick={() => {
                        handleConfigureClick(item);
                      }}
                      tabIndex={0}
                    />
                  </React.Fragment>
                ))}
              </>
            }
            trigger={<Icon kind='MoreHorizontal' size='medium' />}
          />
        )}
      </Box>
      {provider && (
        <ApplicationIcon
          logo={provider.icon}
          className='Ext-IDPProviders-contentWrapper-content-card-icon'
          size='xxLarge'
        />
      )}
      <Typography kind='h3' color='--color-foreground-neutral-bold'>
        {provider?.name}
      </Typography>
      {item.status === 0 && provider && enableModal()}
      {item.status === 1 && provider && disableModal()}
      {item.status === 2 && provider && setUpHandler()}
    </Box>
  );
}

export default ProviderCard;
